import { useEffect, useState } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { CategoryProduct } from '../Shop/Categories'
import { ProductCard } from '../Product'
import { useConsumerCart, useMediaQueries } from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'

type UpsellProductsListProps = {
  upsellProducts: CategoryProduct[]
  onProductsUpdate: (products: CategoryProduct[]) => void
  layout: 'grid' | 'carousel'
  onAllProductsAdded?: () => void
}

export const UpsellProductsList = ({
  upsellProducts,
  onProductsUpdate,
  layout,
  onAllProductsAdded
}: UpsellProductsListProps) => {
  const { cart } = useConsumerCart()
  const { isMobile } = useMediaQueries()
  const [productsAdded, setProductsAdded] = useState<CategoryProduct['id'][]>(
    []
  )
  const staticProductCardLineClamp = layout === 'carousel' && isMobile
  // Listen to cart updates and create a list of products that have been added from the upsell.
  useEffect(() => {
    if (!cart?.orderItems.length) return

    const upsellProductsInCart = cart.orderItems
      .filter(({ product }) =>
        upsellProducts.some((upsellItem) => upsellItem.id === product.id)
      )
      .map(({ product }) => product.id)
    setProductsAdded(upsellProductsInCart)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.orderItems])

  /** Remove added products from the list after animation completes */
  useEffect(() => {
    if (!!productsAdded.length) {
      setTimeout(() => {
        const updatedUpsellProducts = upsellProducts.filter(
          (upsellProduct) => !productsAdded.includes(upsellProduct.id)
        )

        // no more products to add, trigger callback
        if (!updatedUpsellProducts.length && onAllProductsAdded) {
          onAllProductsAdded()
        }
        onProductsUpdate(updatedUpsellProducts)
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsAdded])

  return (
    <ProductsList layout={layout}>
      {upsellProducts.map((product, index) => {
        const hasBeenAdded = productsAdded.includes(product.id)

        return (
          <ProductsListItem
            key={product.slug}
            hasBeenAdded={hasBeenAdded}
            layout={layout}
          >
            <ProductAddedCover hasBeenAdded={hasBeenAdded} />
            <ProductCard
              product={product}
              productCategory={'upsell'}
              isLast={index === upsellProducts.length - 1}
              isStoreAcceptingOrders={true}
              staticLineClamp={staticProductCardLineClamp}
              source={
                layout === 'grid'
                  ? 'MOST_POPULAR_UPSELL_MODAL'
                  : 'MOST_POPULAR_UPSELL_CART'
              }
              contentDimensions={
                layout === 'carousel'
                  ? { mobile: '100px', desktop: '100px' }
                  : undefined
              }
            />
          </ProductsListItem>
        )
      })}
    </ProductsList>
  )
}

const ProductsList = styled.ul<
  StyledHTMLElement & { layout: 'grid' | 'carousel' }
>(({ theme, layout }): CSSObject => {
  const baseStyles: CSSObject = {
    margin: 0,
    padding: 0,
    listStyle: 'none'
  }

  if (layout === 'carousel') {
    return {
      ...baseStyles,
      display: 'flex',
      flexDirection: 'row',
      gap: '16px'
    }
  }

  // grid layout
  return {
    ...baseStyles,
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    [theme.mediaQueries.viewport7]: {
      gap: '16px'
    }
  }
})

/** Product list item with removal animation. Animation timing: 0.6s delay + 0.3s transition = 0.9s total
 * Product is removed from DOM at 1.0s (see useEffect above) */
const ProductsListItem = styled.li<{
  hasBeenAdded: boolean
  layout: 'grid' | 'carousel'
}>(({ theme, hasBeenAdded, layout }) => {
  const baseStyles: CSSObject = {
    position: 'relative',
    opacity: hasBeenAdded ? 0 : 1,
    overflow: 'hidden',
    transition: 'all 0.3s ease-in',
    transitionDelay: '0.6s',
    '& a': {
      zIndex: 1,
      pointerEvents: hasBeenAdded ? 'none' : 'auto'
    }
  }

  if (layout === 'carousel') {
    return {
      ...baseStyles,
      height: '138px',
      width: hasBeenAdded ? 0 : '358px',
      flexShrink: 0,
      scrollSnapAlign: 'center',

      // override child ProductCard styles
      hr: {
        display: 'none'
      },
      '[data-testid="product-card"]': {
        border: '2px solid #FAFAFA',
        padding: '12px'
      }
    }
  }

  // grid layout
  return {
    ...baseStyles,
    height: hasBeenAdded ? 0 : '141px',
    [theme.mediaQueries.viewport7]: {
      height: hasBeenAdded ? 0 : '154px'
    }
  }
})

const ProductAddedCover = styled.div<{ hasBeenAdded: boolean }>(
  ({ hasBeenAdded }): CSSObject => ({
    display: 'flex',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: hasBeenAdded ? 2 : 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAFAFA',
    opacity: hasBeenAdded ? 0.9 : 0,
    transition: `opacity 0.3s ease-in`
  })
)
